import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type {
  CreateEscritorioDto,
  Escritorio,
  EscritorioFilters,
  UpdateEscritorioDto,
} from '@/types/escritorio/escritorioCrudTypes';

export const escritorioService = {
  listarWorkspaces: async (): Promise<{ data: Escritorio[]; total: number }> => {
    const response = await apiGet(`/escritorios/workspaces`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar workspaces' }));
      throw new Error(errorData.error || 'Erro ao listar workspaces');
    }
    const result = await response.json();
    const arr = Array.isArray(result.data) ? result.data : result.data?.data || [];
    return { data: arr, total: arr.length };
  },

  buscarPessoal: async (): Promise<Escritorio | null> => {
    const response = await apiGet(`/escritorios/pessoal`);
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao buscar workspace pessoal' }));
      throw new Error(errorData.error || 'Erro ao buscar workspace pessoal');
    }
    const result = await response.json();
    return result.data || null;
  },
  listar: async (filters?: EscritorioFilters): Promise<{ data: Escritorio[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.ativo !== undefined) params.append('ativo', String(filters.ativo));
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/escritorios${query}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao listar escritórios' }));
      throw new Error(errorData.error || 'Erro ao listar escritórios');
    }

    const result = await response.json();
    return {
      data: result.data.data || result.data,
      total: result.data.pagination?.total || result.data.length || 0,
    };
  },

  buscarPorId: async (id: string): Promise<Escritorio> => {
    const response = await apiGet(`/escritorios/${id}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Escritório não encontrado' }));
      throw new Error(errorData.error || 'Escritório não encontrado');
    }

    const result = await response.json();
    return result.data;
  },

  criar: async (data: CreateEscritorioDto): Promise<Escritorio> => {
    const response = await apiPost('/escritorios/team', data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao criar escritório' }));
      throw new Error(errorData.error || 'Erro ao criar escritório');
    }

    const result = await response.json();
    return result.data;
  },

  atualizar: async (id: string, data: UpdateEscritorioDto): Promise<Escritorio> => {
    const response = await apiPut(`/escritorios/${id}`, data);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao atualizar escritório' }));
      throw new Error(errorData.error || 'Erro ao atualizar escritório');
    }

    const result = await response.json();
    return result.data;
  },

  excluir: async (id: string): Promise<void> => {
    const response = await apiDelete(`/escritorios/${id}`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao excluir escritório' }));
      throw new Error(errorData.error || 'Erro ao excluir escritório');
    }
  },

  checkNome: async (nome: string): Promise<{ exists: boolean }> => {
    const response = await apiGet(`/escritorios/check-nome?nome=${encodeURIComponent(nome)}`);

    if (!response.ok) {
      throw new Error('Erro ao verificar nome');
    }

    const result = await response.json();
    return result.data;
  },

  checkSubdominio: async (subdominio: string): Promise<{ exists: boolean }> => {
    const response = await apiGet(
      `/escritorios/check-subdominio?subdominio=${encodeURIComponent(subdominio)}`,
    );

    if (!response.ok) {
      throw new Error('Erro ao verificar subdomínio');
    }

    const result = await response.json();
    return result.data;
  },

  updateLastWorkspace: async (_userId: string, workspaceId: string): Promise<void> => {
    const response = await apiPatch(`/me/contexto`, { tipo: 'WORKSPACE', id: workspaceId });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao atualizar último workspace' }));
      throw new Error(errorData.error || 'Erro ao atualizar último workspace');
    }
  },
};
